import { ReportTypeEnum } from '@nx-smartmonkey/shared/domain';
import { STOP_REPORT_STATUS, StopStatus } from '../constants/Stops';
import { StopReport } from '../domain/report/StopReport';
import { StopReportCreate } from '../domain/report/StopReportCreate';
import { reportRepository } from '../repositories/report';
import { ReportRepository } from '../repositories/report/ReportRepository';
import { network } from '../services';
import { NetworkInterface } from '../services/network/network';
import { reportQueueService, ReportQueueService } from './ReportsQueueService';

export const ReportServiceErrors = { RetryError: `Failed retry attempt`, AlreadyQueuedError: `Report already queued` };

export interface ReportCreatedProps {
  queued: boolean;
  stopReport: StopReport;
  stopStatus: StopStatus;
}

class ReportService {
  constructor(
    private reportRepository: ReportRepository,
    private network: NetworkInterface,
    private reportQueue: ReportQueueService
  ) {}

  async createReport(stopReportCreate: StopReportCreate): Promise<ReportCreatedProps> {
    if (this.network.isOnline()) {
      try {
        const stopReport = await this.reportRepository.createReport(stopReportCreate);
        return {
          queued: false,
          stopReport,
          stopStatus: stopReportCreate.status,
        };
      } catch (error: any) {
        if (!error.response) {
          return this.addReportToQueue(stopReportCreate);
        }
        throw error;
      }
    } else {
      return this.addReportToQueue(stopReportCreate);
    }
  }

  async retryCreateReport(stopReportCreate: StopReportCreate): Promise<ReportCreatedProps> {
    // Keeping the try catch inside, we can make sure we manage network errors gracefully
    const stopReport = await this.reportRepository.createReport(stopReportCreate).catch(async (error) => {
      if (!error.response) throw new Error(ReportServiceErrors.RetryError); // If no response, we assume it's a network error and try to queue the report
      throw error;
    });

    return {
      queued: false,
      stopReport: stopReport,
      stopStatus: stopReportCreate.status,
    };
  }

  private async addReportToQueue(stopReportCreate: StopReportCreate) {
    if (await this.reportQueue.getPendingReport(stopReportCreate.stopId))
      throw new Error(ReportServiceErrors.AlreadyQueuedError);

    await this.reportQueue.addReport(stopReportCreate);

    const customFields = stopReportCreate.reportData?.customFields?.getValue() ?? {};

    const stopReport = StopReport.create(
      {
        stop_id: stopReportCreate.stopId,
        type:
          stopReportCreate.status === STOP_REPORT_STATUS.canceled
            ? ReportTypeEnum.SERVICE_REPORT_CANCELED
            : ReportTypeEnum.SERVICE_REPORT_COMPLETED,
        comments: (stopReportCreate.reportData?.comments ?? ``) !== ``,
        images: (stopReportCreate.images ?? []).length > 0 ? true : false,
        signature: stopReportCreate.reportData?.signature ? true : false,
        custom_fields: Object.keys(customFields).length > 0 ? true : false,
        tasks_completed:
          (stopReportCreate.reportData?.reportTasks ?? []).filter((task) => task.status === `completed`).length +
          (stopReportCreate.reportData?.temporalReportTasks ?? []).filter((task) => task.status === `completed`).length,
        tasks_total:
          (stopReportCreate.reportData?.reportTasks ?? []).length +
          (stopReportCreate.reportData?.temporalReportTasks ?? []).length,
        created_at: stopReportCreate.createdAt,
      },
      stopReportCreate.id
    );

    return { queued: true, stopReport, stopStatus: stopReportCreate.status };
  }

  async discardReport({ stopReport }: { stopReport: StopReport }): Promise<void> {
    await this.reportRepository.discardReport(stopReport);
  }
}

export const reportService = new ReportService(reportRepository, network, reportQueueService);
